import React, { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { X, Package, Cog, Calculator, Percent, Hash, FileText, Plus, Edit3 } from 'lucide-react';

const taxRateOptions = [
  { value: '0', label: '0%' },
  { value: '6', label: '6%' },
  { value: '12', label: '12%' },
  { value: '25', label: '25%' }
];

const unitOptions = [
  { value: 'Piece', label: 'Styck' },
  { value: 'Hour', label: 'Timmar' },
  { value: 'Liter', label: 'Liter' },
  { value: 'Kilogram', label: 'Kilogram' },
  { value: 'Square Meter', label: 'Kvadratmeter' },
  { value: 'Cubic Meter', label: 'Kubikmeter' },
  { value: 'Meter', label: 'Meter' },
  { value: 'Package', label: 'Förpackning' },
  { value: 'Set', label: 'Set' },
  { value: 'Day', label: 'Dag' },
  { value: 'Month', label: 'Månad' }
];

const typeOptions = [
  { value: 'Goods', label: 'Varor' },
  { value: 'Service', label: 'Tjänst' }
];

// Enhanced Yup Validation Schema
const validationSchema = Yup.object().shape({
  productcode: Yup.string()
    .min(2, 'Produktkod måste vara minst 2 tecken')
    .required('Produktkod är obligatorisk'),
  description: Yup.string()
    .min(3, 'Beskrivning måste vara minst 3 tecken')
    .required('Beskrivning är obligatorisk'),
  unitprice: Yup.number()
    .typeError('Pris måste vara ett giltigt nummer')
    .positive('Pris måste vara positivt')
    .required('Pris är obligatoriskt'),
  taxrate: Yup.string().required('Moms är obligatorisk'),
  unit: Yup.string().required('Enhet är obligatorisk'),
  type: Yup.string().required('Typ är obligatorisk')
});

const NewArticleModal = ({ show, handleClose, handleSave, handleUpdate, articleDetails }) => {
  const isEditMode = articleDetails !== null;

  const formik = useFormik({
    initialValues: {
      productcode: '',
      description: '',
      unitprice: '',
      type: 'Goods',
      unit: 'Piece',
      taxrate: '25'
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        productcode: values.productcode,
        description: values.description,
        unitprice: parseFloat(values.unitprice),
        type: values.type,
        unit: values.unit,
        taxrate: parseInt(values.taxrate)
      };

      if (isEditMode && handleUpdate) {
        handleUpdate(articleDetails.id, payload);
      } else {
        handleSave(payload);
      }
    }
  });

  const { values, errors, touched, setFieldValue, handleChange, handleSubmit, resetForm } = formik;

  useEffect(() => {
    if (articleDetails && show) {
      resetForm({
        values: {
          productcode: articleDetails.productcode || '',
          description: articleDetails.description || '',
          unitprice: articleDetails.unitprice || '',
          type: articleDetails.type || 'Goods',
          unit: articleDetails.unit || 'Piece',
          taxrate: String(articleDetails.tax_rate || 25)
        }
      });
    } else if (show && !articleDetails) {
      resetForm({
        values: {
          productcode: '',
          description: '',
          unitprice: '',
          type: 'Goods',
          unit: 'Piece',
          taxrate: '25'
        }
      });
    }
  }, [articleDetails, show, resetForm]);

  const handleCloseModal = () => {
    resetForm();
    handleClose();
  };

  return (
    <>
      <style>{`
        .article-modal-content {
          border-radius: 8px;
          border: none;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        
        .article-modal-header {
          background: white;
          color: #1e293b;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }
        
        .article-modal-body {
          padding: 2rem;
          background: #ffffff;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .section-card {
          background: transparent;
          border: none;
          padding: 0;
          margin-bottom: 2rem;
        }
        
        .section-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          padding: 0;
          border: none;
          background: transparent;
        }
        
        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        
        .section-subtitle {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0;
        }
        
        .type-selector {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .type-option {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
        }
        
        .type-option:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        
        .type-option.selected {
          border-color: #3b82f6;
          background: #dbeafe;
        }
        
        .type-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          background: #f1f5f9;
          color: #64748b;
          transition: all 0.2s ease;
        }
        
        .type-option.selected .type-icon {
          background: #3b82f6;
          color: white;
        }
        
        .type-label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }
        
        .type-description {
          font-size: 0.875rem;
          color: #64748b;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }
        
        .form-label.required::after {
          content: " *";
          color: #ef4444;
        }
        
        .form-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }
        
        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        
        .form-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);
        }
        
        .error-message {
          font-size: 0.75rem;
          color: #ef4444;
          margin-top: 0.25rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        
        .form-grid {
          display: grid;
          gap: 1rem;
        }
        
        .grid-2 {
          grid-template-columns: 1fr 1fr;
        }
        
        .modal-actions {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .action-button {
          border-radius: 6px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .btn-cancel {
          background: #f1f5f9;
          color:rgb(100, 139, 105);
          border: 1px solid #e2e8f0;
        }
        
        .btn-cancel:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .btn-save {
          background:var(--bs-primary);
          color: white;
        }
        
        .btn-save:hover {
          background: var(--bs-green);
        }
        
        .close-button {
          position: absolute;
          top: 1rem;
          right: 1.5rem;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .close-button:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .price-input-wrapper {
          position: relative;
        }
        
        .price-input-wrapper::after {
          content: "kr";
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
          font-size: 0.875rem;
          pointer-events: none;
        }
        
        .price-input {
          padding-right: 40px;
        }
        
        @media (max-width: 768px) {
          .type-selector {
            grid-template-columns: 1fr;
          }
          
          .grid-2 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <Modal
        className='fade'
        size='lg'
        show={show}
        onHide={handleCloseModal}
        backdrop="static"
        centered
      >
        <div className="article-modal-content">
          <FormikProvider value={formik}>
            <form onSubmit={handleSubmit}>
              {/* Enhanced Header */}
              <div className="article-modal-header">
                <button type="button" className="close-button" onClick={handleCloseModal}>
                  <X size={16} />
                </button>
                <div className="d-flex align-items-center gap-3">
                  {values.type === 'Goods' ? <Package size={24} /> : <Cog size={24} />}
                  <div>
                    <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                      {isEditMode ? 'Redigera artikel' : 'Ny artikel'}
                    </h3>
                    <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                      {isEditMode ? 'Uppdatera artikelinformation' : 'Skapa en ny artikel i systemet'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Enhanced Body */}
              <div className="article-modal-body">
                {/* Article Type Selection */}
                <div className="section-card">
                  <div className="section-header">
                    <Package size={20} />
                    <div>
                      <h4 className="section-title">Artikeltyp</h4>
                      <p className="section-subtitle">Välj typ av artikel du vill skapa</p>
                    </div>
                  </div>

                  <div className="type-selector">
                    <div
                      className={`type-option ${values.type === 'Goods' ? 'selected' : ''}`}
                      onClick={() => setFieldValue('type', 'Goods')}
                    >
                      <div className="type-icon">
                        <Package size={24} />
                      </div>
                      <div className="type-label">Varor</div>
                      <div className="type-description">Fysiska produkter och material</div>
                    </div>

                    <div
                      className={`type-option ${values.type === 'Service' ? 'selected' : ''}`}
                      onClick={() => setFieldValue('type', 'Service')}
                    >
                      <div className="type-icon">
                        <Cog size={24} />
                      </div>
                      <div className="type-label">Tjänster</div>
                      <div className="type-description">Immateriella tjänster och arbete</div>
                    </div>
                  </div>

                  {touched.type && errors.type && (
                    <div className="error-message">⚠ {errors.type}</div>
                  )}
                </div>

                {/* Basic Information */}
                <div className="section-card">
                  <div className="section-header">
                    <FileText size={20} />
                    <div>
                      <h4 className="section-title">Grundläggande information</h4>
                      <p className="section-subtitle">Artikelns identifiering och beskrivning</p>
                    </div>
                  </div>

                  <div className="form-grid grid-2">
                    <div className="form-group">
                      <label className="form-label required">Produktkod</label>
                      <input
                        type="text"
                        name="productcode"
                        className={`form-input ${touched.productcode && errors.productcode ? 'error' : ''}`}
                        value={values.productcode}
                        onChange={handleChange}
                        placeholder="Ange produktkod"
                      />
                      {touched.productcode && errors.productcode && (
                        <div className="error-message">⚠ {errors.productcode}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Enhet</label>
                      <Select
                        options={unitOptions}
                        value={unitOptions.find(option => option.value === values.unit)}
                        onChange={(selectedOption) => setFieldValue('unit', selectedOption.value)}
                        placeholder="Välj enhet"
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            border: `1px solid ${touched.unit && errors.unit ? '#ef4444' : '#d1d5db'}`,
                            borderRadius: '6px',
                            minHeight: '46px',
                            boxShadow: state.isFocused ?
                              (touched.unit && errors.unit ? '0 0 0 2px rgba(239, 68, 68, 0.1)' : '0 0 0 2px rgba(59, 130, 246, 0.1)') :
                              'none'
                          }),
                        }}
                      />
                      {touched.unit && errors.unit && (
                        <div className="error-message">⚠ {errors.unit}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label required">Beskrivning</label>
                    <input
                      type="text"
                      name="description"
                      className={`form-input ${touched.description && errors.description ? 'error' : ''}`}
                      value={values.description}
                      onChange={handleChange}
                      placeholder="Ange artikelbeskrivning"
                    />
                    {touched.description && errors.description && (
                      <div className="error-message">⚠ {errors.description}</div>
                    )}
                  </div>
                </div>

                {/* Pricing Information */}
                <div className="section-card">
                  <div className="section-header">
                    <Calculator size={20} />
                    <div>
                      <h4 className="section-title">Prissättning</h4>
                      <p className="section-subtitle">Pris och momsinformation</p>
                    </div>
                  </div>

                  <div className="form-grid grid-2">
                    <div className="form-group">
                      <label className="form-label required">Pris (exkl. moms)</label>
                      <div className="price-input-wrapper">
                        <input
                          type="number"
                          name="unitprice"
                          className={`form-input price-input ${touched.unitprice && errors.unitprice ? 'error' : ''}`}
                          value={values.unitprice}
                          onChange={handleChange}
                          placeholder="0,00"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      {touched.unitprice && errors.unitprice && (
                        <div className="error-message">⚠ {errors.unitprice}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Moms</label>
                      <Select
                        options={taxRateOptions}
                        value={taxRateOptions.find(option => option.value === values.taxrate)}
                        onChange={(selectedOption) => setFieldValue('taxrate', selectedOption.value)}
                        placeholder="Välj momssats"
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            border: `1px solid ${touched.taxrate && errors.taxrate ? '#ef4444' : '#d1d5db'}`,
                            borderRadius: '6px',
                            minHeight: '46px',
                            boxShadow: state.isFocused ?
                              (touched.taxrate && errors.taxrate ? '0 0 0 2px rgba(239, 68, 68, 0.1)' : '0 0 0 2px rgba(59, 130, 246, 0.1)') :
                              'none'
                          }),
                        }}
                      />
                      {touched.taxrate && errors.taxrate && (
                        <div className="error-message">⚠ {errors.taxrate}</div>
                      )}
                    </div>
                  </div>

                  {/* Price Preview */}
                  {values.unitprice && values.taxrate && (
                    <div
                      style={{
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginTop: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '0.875rem', color: '#059669', fontWeight: '500' }}>
                        Prisförhandsvisning:
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#065f46', marginTop: '0.25rem' }}>
                        Pris exkl. moms: {parseFloat(values.unitprice).toLocaleString('sv-SE')} kr<br />
                        Moms ({values.taxrate}%): {(parseFloat(values.unitprice) * (parseInt(values.taxrate) / 100)).toLocaleString('sv-SE')} kr<br />
                        <strong>Totalt inkl. moms: {(parseFloat(values.unitprice) * (1 + parseInt(values.taxrate) / 100)).toLocaleString('sv-SE')} kr</strong>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Footer */}
              <div className="modal-actions">
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  {isEditMode ? 'Ändringar sparas automatiskt' : 'Alla obligatoriska fält måste fyllas i'}
                </div>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="action-button btn-cancel"
                    onClick={handleCloseModal}
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="action-button btn-save"
                  >
                    {isEditMode ? (
                      <>
                        <Edit3 size={16} />
                        Uppdatera artikel
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Skapa artikel
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </Modal>
    </>
  );
};

export default NewArticleModal;




// import React, { useEffect } from 'react';
// import { Modal, Button, Form } from 'react-bootstrap';
// import Select from 'react-select';
// import { useFormik, FormikProvider } from 'formik';
// import * as Yup from 'yup';

// const taxRateOptions = [
//   { value: '0%', label: '0%' },
//   { value: '6%', label: '6%' },
//   { value: '12%', label: '12%' },
//   { value: '25%', label: '25%' }
// ];

// const unitOptions = [
//   { value: 'Piece', label: 'Styck' },
//   { value: 'Hour', label: 'Timmar' },
//   { value: 'Liter', label: 'Liter' },
//   { value: 'Kilogram', label: 'Kilogram' },
//   { value: 'Square Meter', label: 'Kvadratmeter' },
//   { value: 'Cubic Meter', label: 'Kubikmeter' },
//   { value: 'Meter', label: 'Meter' },
//   { value: 'Package', label: 'Förpackning' },
//   { value: 'Set', label: 'Set' },
//   { value: 'Day', label: 'Dag' },
//   { value: 'Month', label: 'Månad' }
// ];

// const typeOptions = [
//   { value: 'Goods', label: 'Varor' },
//   { value: 'Service', label: 'Tjänst' }
// ];

// // Yup Validation Schema
// const validationSchema = Yup.object().shape({
//   productcode: Yup.string().required('Produktkod är obligatorisk'),
//   description: Yup.string().required('Beskrivning är obligatorisk'),
//   unitprice: Yup.number()
//     .typeError('Pris måste vara ett nummer')
//     .required('Pris är obligatoriskt'),
//   taxrate: Yup.string().required('Välj moms'),
//   unit: Yup.string().required('Välj enhet'),
//   type: Yup.string().required('Välj typ')
// });

// const NewArticleModal = ({ show, handleClose, handleSave, handleUpdate, articleDetails }) => {
//   const isNewArticle = articleDetails === null;

//   const formik = useFormik({
//     initialValues: {
//       productcode: '',
//       description: '',
//       unitprice: '',
//       type: '',
//       unit: '',
//       taxrate: ''
//     },
//     validationSchema: validationSchema,
//     onSubmit: (values) => {
//       const payload = {
//         productcode: values.productcode,
//         description: values.description,
//         unitprice: values.unitprice,
//         type: values.type,
//         unit: values.unit,
//         taxrate: values.taxrate
//       };

//       if (isNewArticle || !handleUpdate) {
//         handleSave(payload);
//       } else {
//         handleUpdate(articleDetails.id, payload);
//       }

//       handleClose();
//     }
//   });

//   const { values, errors, touched, setFieldValue, handleChange, handleSubmit, resetForm } = formik;

//   useEffect(() => {
//     if (articleDetails) {
//       resetForm({
//         values: {
//           productcode: articleDetails.productcode || '',
//           description: articleDetails.description || '',
//           unitprice: articleDetails.unitprice || '',
//           type: articleDetails.type || '',
//           unit: articleDetails.unit || '',
//           taxrate: articleDetails.tax_rate || ''
//         }
//       });
//     } else {
//       resetForm({
//         values: {
//           productcode: '',
//           description: '',
//           unitprice: '',
//           type: '',
//           unit: '',
//           taxrate: ''
//         }
//       });
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [articleDetails, show]);

//   const handleCloseClick = () => {
//     resetForm();
//     handleClose();
//   };

//   return (
//     <Modal className='fade bd-example-modal-lg' size='lg' show={show} onHide={handleClose}>
//       <FormikProvider value={formik}>
//         <form onSubmit={handleSubmit}>
//           <Modal.Header closeButton>
//             <Modal.Title>{isNewArticle ? 'Ny artikel' : 'Redigera artikel'}</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             <div className="basic-form">
//               <div className='col-xl-12'>
//                 <div className="col-xl-4 mb-5">
//                   Fyll i information om artikeln.
//                 </div>
//                 <Form.Group className="mb-3" controlId="productcode">
//                   <Form.Label>Produktkod *</Form.Label>
//                   <Form.Control
//                     type="text"
//                     name="productcode"
//                     value={values.productcode}
//                     onChange={handleChange}
//                   />
//                   {touched.productcode && errors.productcode && (
//                     <div className="text-danger">{errors.productcode}</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-3" controlId="description">
//                   <Form.Label>Beskrivning *</Form.Label>
//                   <Form.Control
//                     type="text"
//                     name="description"
//                     value={values.description}
//                     onChange={handleChange}
//                   />
//                   {touched.description && errors.description && (
//                     <div className="text-danger">{errors.description}</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-3" controlId="unitprice">
//                   <Form.Label>Pris (exkl. Moms) *</Form.Label>
//                   <Form.Control
//                     type="text"
//                     name="unitprice"
//                     value={values.unitprice}
//                     onChange={handleChange}
//                   />
//                   {touched.unitprice && errors.unitprice && (
//                     <div className="text-danger">{errors.unitprice}</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-3" controlId="tax_rate">
//                   <Form.Label>Moms *</Form.Label>
//                   <Select
//                     options={taxRateOptions}
//                     value={taxRateOptions.find(option => option.value === values.taxrate)}
//                     onChange={(selectedOption) => setFieldValue('taxrate', selectedOption.value)}
//                   />
//                   {touched.taxrate && errors.taxrate && (
//                     <div className="text-danger">{errors.taxrate}</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-3" controlId="enhet">
//                   <Form.Label>Enhet *</Form.Label>
//                   <Select
//                     options={unitOptions}
//                     value={unitOptions.find(option => option.value === values.unit)}
//                     onChange={(selectedOption) => setFieldValue('unit', selectedOption.value)}
//                   />
//                   {touched.unit && errors.unit && (
//                     <div className="text-danger">{errors.unit}</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-3" controlId="type">
//                   <Form.Label>Typ *</Form.Label>
//                   <Select
//                     options={typeOptions}
//                     value={typeOptions.find(option => option.value === values.type)}
//                     onChange={(selectedOption) => setFieldValue('type', selectedOption.value)}
//                   />
//                   {touched.type && errors.type && (
//                     <div className="text-danger">{errors.type}</div>
//                   )}
//                 </Form.Group>
//               </div>
//             </div>
//           </Modal.Body>
//           <Modal.Footer>
//             <Button variant="secondary" onClick={handleCloseClick}>
//               Avbryt
//             </Button>
//             <Button variant="primary" type="submit">
//               Spara
//             </Button>
//           </Modal.Footer>
//         </form>
//       </FormikProvider>
//     </Modal>
//   );
// };

// export default NewArticleModal;
